/**
 * Friday theme → Mermaid theme.
 *
 * Mermaid draws a diagram with its own palette. To make a diagram look
 * like part of the page we map Friday's tokens onto Mermaid's
 * `themeVariables`. The theme *family* decides light vs dark; the tokens
 * are RGB triplets, so they are converted to hex for Mermaid.
 */

import { tokenToHex, type ThemeDefinition } from '@/features/theme/themes'

export interface MermaidTheme {
  /** Friday theme id — the renderer re-initialises when it changes. */
  id: string
  /** True for the dark theme families. */
  dark: boolean
  /** Mermaid `themeVariables`. */
  variables: Record<string, string>
}

/** The theme families that are dark. `pulp`, `light` and `mono` are light. */
const DARK_FAMILIES = new Set(['neon', 'film', 'game'])

export function mermaidThemeFor(theme: ThemeDefinition): MermaidTheme {
  const dark = DARK_FAMILIES.has(theme.family)
  const tokens = theme.tokens
  const fg = tokenToHex(tokens.fg)
  const bg = tokenToHex(tokens.bg)
  const bgElevated = tokenToHex(tokens.bgElevated)
  const bgSubtle = tokenToHex(tokens.bgSubtle)
  const border = tokenToHex(tokens.border)

  const variables: Record<string, string> = {
    background: bg,
    mainBkg: bgElevated,
    secondBkg: bgSubtle,
    tertiaryBkg: bgSubtle,
    primaryColor: bgSubtle,
    primaryTextColor: fg,
    primaryBorderColor: border,
    secondaryColor: bgElevated,
    secondaryTextColor: fg,
    secondaryBorderColor: border,
    tertiaryColor: bgElevated,
    tertiaryTextColor: fg,
    tertiaryBorderColor: border,
    lineColor: border,
    textColor: fg,
    titleColor: fg,
    nodeBorder: border,
    clusterBkg: bgElevated,
    clusterBorder: border,
    edgeLabelBackground: bg,
    labelBoxBkgColor: bgElevated,
    labelBoxBorderColor: border,
    labelTextColor: fg,
    noteBkgColor: bgElevated,
    noteBorderColor: border,
    noteTextColor: fg,
  }

  // The pie diagram keeps Mermaid's own section colours — Friday's accent
  // would make every slice the same shade — but its text follows the page.
  variables.pieTitleTextColor = fg
  variables.pieSectionTextColor = fg
  variables.pieLegendTextColor = fg

  if (tokens.fontFamily) variables.fontFamily = tokens.fontFamily
  variables.nodeTextColor = fg
  if (dark) {
    variables.edgeLabelBackground = bg
  }

  return { id: theme.id, dark, variables }
}
